/**
 * P9.3 (Juni 2026) – Tarifvergleich + Tarifwechsel-ANFRAGE.
 *
 * STRIKT OHNE Stripe/Checkout/Geldfluss: Der Nutzer vergleicht die Tarife,
 * wählt einen aus und bestätigt ausdrücklich. Dadurch wird NUR eine
 * `plan_change_request` erstellt – KEIN echter Tarifwechsel, KEINE Zahlung.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Loader2 } from "lucide-react";

function euro(cents) {
  if (cents == null) return "–";
  return (
    (cents / 100).toLocaleString("de-DE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " €"
  );
}

export default function TarifvergleichDialog({ open, onOpenChange, onRequested }) {
  const [data, setData] = useState(null);
  const [interval, setIntervalSel] = useState("yearly");
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setData(null);
      api
        .getPlanComparison()
        .then((d) => {
          setData(d);
          setIntervalSel(d.billing_interval || "yearly");
        })
        .catch(() => toast.error("Tarife konnten nicht geladen werden."));
    }
  }, [open]);

  const submit = async () => {
    if (!confirmPlan) return;
    setSubmitting(true);
    try {
      await api.createPlanChangeRequest({
        requested_plan: confirmPlan.plan_id,
        requested_interval: confirmPlan.is_enterprise ? null : interval,
        consent: true,
      });
      toast.success("Tarifwechsel-Anfrage eingereicht. Es erfolgt keine Zahlung.");
      setConfirmPlan(null);
      onOpenChange(false);
      onRequested && onRequested();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anfrage fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="tarifvergleich-dialog">
          <DialogHeader>
            <DialogTitle>Tarif wechseln</DialogTitle>
            <DialogDescription>
              Wählen Sie Ihren gewünschten Tarif. Es wird nur eine{" "}
              <strong>Wechsel-Anfrage</strong> erstellt – es erfolgt keine Zahlung.
              Der Tarifwechsel wird erst nach Freigabe/Zahlungsaktivierung wirksam.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="tarif-interval-toggle">
            <span className="text-sm text-slate-600" data-testid="tarif-dialog-current">
              Aktueller Tarif: <strong className="text-slate-900">{data?.current_plan_name || "–"}</strong>
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={interval === "monthly" ? "default" : "outline"}
                onClick={() => setIntervalSel("monthly")}
                data-testid="tarif-interval-monthly"
              >
                Monatlich
              </Button>
              <Button
                size="sm"
                variant={interval === "yearly" ? "default" : "outline"}
                onClick={() => setIntervalSel("yearly")}
                data-testid="tarif-interval-yearly"
              >
                Jährlich · 2 Monate sparen
              </Button>
            </div>
          </div>

          {!data ? (
            <div className="py-10 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Lade Tarife…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.plans.map((p) => {
                const price =
                  interval === "yearly" ? p.yearly_price_net_cents : p.monthly_price_net_cents;
                return (
                  <div
                    key={p.plan_id}
                    data-testid={`tarif-card-${p.plan_id}`}
                    className={`rounded-lg border p-4 flex flex-col gap-2 ${
                      p.is_current
                        ? "border-indigo-400 bg-indigo-50"
                        : p.is_recommended
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-slate-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-heading font-semibold text-slate-900">{p.name}</span>
                      {p.is_current && (
                        <Badge data-testid={`tarif-badge-current-${p.plan_id}`} className="bg-indigo-600 text-white">
                          Aktuell
                        </Badge>
                      )}
                      {!p.is_current && p.is_recommended && (
                        <Badge data-testid={`tarif-badge-recommended-${p.plan_id}`} className="bg-emerald-600 text-white">
                          <Star className="w-3 h-3 mr-1" />
                          Empfohlen
                        </Badge>
                      )}
                    </div>

                    <div className="text-xs text-slate-500">
                      {p.seat_max == null
                        ? `ab ${p.seat_min} Mitarbeiter`
                        : `${p.seat_min}–${p.seat_max} aktive Mitarbeiter`}
                    </div>

                    {p.is_enterprise ? (
                      <div className="text-lg font-bold text-slate-900">Individuelles Angebot</div>
                    ) : (
                      <div>
                        <div className="text-2xl font-bold text-slate-900">
                          {euro(price)}
                          <span className="text-xs font-normal text-slate-400">
                            /{interval === "yearly" ? "Jahr" : "Monat"}
                          </span>
                        </div>
                        {interval === "yearly" && p.yearly_savings_net_cents ? (
                          <div className="text-xs text-emerald-600">
                            Sie sparen {euro(p.yearly_savings_net_cents)}/Jahr
                          </div>
                        ) : null}
                        <div className="text-xs text-slate-400">netto zzgl. USt</div>
                      </div>
                    )}

                    <div className="mt-auto pt-2">
                      {p.is_current ? (
                        <Button variant="outline" disabled className="w-full" data-testid={`tarif-select-${p.plan_id}`}>
                          <Check className="w-4 h-4 mr-1" />
                          Aktueller Tarif
                        </Button>
                      ) : p.change_allowed ? (
                        <Button className="w-full" onClick={() => setConfirmPlan(p)} data-testid={`tarif-select-${p.plan_id}`}>
                          {p.is_enterprise ? "Angebot anfragen" : "Diesen Tarif anfragen"}
                        </Button>
                      ) : (
                        <div>
                          <Button variant="outline" disabled className="w-full" data-testid={`tarif-select-${p.plan_id}`}>
                            Nicht möglich
                          </Button>
                          <p className="text-xs text-amber-700 mt-1" data-testid={`tarif-block-${p.plan_id}`}>
                            {p.change_block_reason}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmPlan} onOpenChange={(o) => { if (!o) setConfirmPlan(null); }}>
        <AlertDialogContent data-testid="tarif-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tarifwechsel anfragen?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmPlan &&
                (confirmPlan.is_enterprise
                  ? "Sie fragen ein individuelles Enterprise-Angebot an."
                  : `Sie fragen den Wechsel zu „${confirmPlan.name}" (${
                      interval === "yearly" ? "jährlich" : "monatlich"
                    }) an.`)}{" "}
              Es erfolgt <strong>keine Zahlung</strong>. Der Wechsel wird erst nach
              Freigabe/Zahlungsaktivierung wirksam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tarif-confirm-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={submit} disabled={submitting} data-testid="tarif-confirm-submit">
              {submitting ? "Sende…" : "Verbindlich anfragen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
